'use client';

/**
 * CallStartNotice — the Turn 16 notice family for the call-start phase
 * machine's non-live states (src/lib/call-phase.ts): the mic-permission
 * wait (16a-alt), the two amber "start failed" reasons (16c), and the calm
 * reconnecting variant (16c, ws_closed). Sits in the SAME "above controls"
 * card slot call-screen.tsx already used for the FB34 placeholder — this
 * file only restyles its contents to the Turn 16 anatomy, it does not move
 * where it sits on screen.
 *
 * Two temperatures, reusing call-notice.tsx's already-built PATIENCE
 * primitives/tokens rather than a parallel implementation:
 *   - SAND (`mic_wait`, `reconnecting`) — the calm "still waiting/working on
 *     its own" register: PatienceSweepTrack (imported, not re-implemented)
 *     + the `patience-in`/`patience-in-reduced` entrance, both verbatim
 *     Turn-15 reuse per the design's own instruction.
 *   - AMBER (`mic_denied`, `unreachable`) — the actionable "something needs
 *     you" register: V3_COLORS' existing amber* tokens (already shared with
 *     call-notice.tsx's WARNING family) + a "Try again" chip wired to
 *     `onRetry`, entering via the existing `call-notice-in` (toastIn).
 *
 * [Deviation from the literal Turn 16 spec] The design's `reconnecting`
 * variant has NO chip — it assumes an external process keeps retrying and
 * only escalates to the amber "Try again" family if that process gives up.
 * The behavior half (useCallMode.ts's `retryCall`) has no such escalation:
 * `ws_closed` is a synchronous, one-shot failure of a manual retry attempt
 * against an already-closed websocket, not an ongoing background
 * reconnection loop — so a duplicate of this card with zero affordance
 * would strand the user with no visible way back to `retryCall()` beyond
 * discovering that End+Call-again happens to work. This file therefore adds
 * a SUBTLE (not the loud amber pill) inline "Try again" for `reconnecting`
 * only, so the calm tone survives while the retry contract stays reachable.
 * See the plan's Turn-16 section / this feature's return report for the
 * full reasoning.
 */

import { Link, Mic, MicOff, RotateCcw, WifiOff } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PRESSABLE, T13_STATUS_COLORS, V3_COLORS } from '@/lib/design-tokens';
import { PatienceSweepTrack } from './call-notice';

export type CallStartNoticeVariant =
  | 'mic_wait'
  | 'reconnecting'
  | 'mic_denied'
  | 'unreachable';

export interface CallStartNoticeProps {
  variant: CallStartNoticeVariant;
  /** "Try again" — rendered for mic_denied/unreachable's amber chip, and
   *  (see file header's deviation note) as a subtle inline link for
   *  `reconnecting`. Never rendered for `mic_wait` (nothing to retry —
   *  the browser prompt itself is the only pending action). */
  onRetry?: () => void;
  /** Turn 15e's release-downward exit — only ever used by `mic_wait` (on
   *  mic-permission grant, see call-screen.tsx) and mirrors CallNotice's own
   *  `releasing` prop exactly. */
  releasing?: boolean;
  className?: string;
}

const COPY: Record<
  CallStartNoticeVariant,
  { title: string; subtitle: string }
> = {
  mic_wait: {
    title: 'Waiting for microphone permission…',
    subtitle: 'Choose "Allow" so Sara can hear you.',
  },
  reconnecting: {
    title: 'Reconnecting to your conversation…',
    subtitle: 'Hang tight — picking up where you left off.',
  },
  mic_denied: {
    title: "Sara can't hear you — microphone access is off.",
    subtitle: 'Allow the mic in your browser, then try again.',
  },
  unreachable: {
    title: "Couldn't reach Sara just now.",
    subtitle: 'This is usually a brief blip — try again.',
  },
};

const SAND_ICON: Record<'mic_wait' | 'reconnecting', LucideIcon> = {
  mic_wait: Mic,
  reconnecting: Link,
};

const AMBER_ICON: Record<'mic_denied' | 'unreachable', LucideIcon> = {
  mic_denied: MicOff,
  unreachable: WifiOff,
};

function isAmberVariant(
  variant: CallStartNoticeVariant
): variant is 'mic_denied' | 'unreachable' {
  return variant === 'mic_denied' || variant === 'unreachable';
}

export function CallStartNotice({
  variant,
  onRetry,
  releasing = false,
  className,
}: CallStartNoticeProps) {
  const copy = COPY[variant];

  if (isAmberVariant(variant)) {
    const Icon = AMBER_ICON[variant];
    return (
      <div
        role="status"
        className={cn(
          'flex items-start gap-[12px] rounded-[14px] border-[1px] px-[14px] py-[13px]',
          'motion-safe:animate-call-notice-in',
          className
        )}
        style={{
          backgroundColor: V3_COLORS.amberBg,
          borderColor: V3_COLORS.amberBorder,
          boxShadow: '0 16px 34px -22px rgba(154,117,52,.6)',
        }}
      >
        <span
          className="flex size-[30px] shrink-0 items-center justify-center rounded-[9px]"
          style={{ backgroundColor: V3_COLORS.amberTile }}
        >
          <Icon
            strokeWidth={2}
            className="size-[16px]"
            style={{ color: V3_COLORS.amber }}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="text-[13px] font-semibold leading-[1.4]"
            style={{ color: V3_COLORS.amberText }}
          >
            {copy.title}
          </div>
          <div
            className="mt-[2px] text-[11.5px] leading-[1.45]"
            style={{ color: V3_COLORS.amberSubtitle }}
          >
            {copy.subtitle}
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={cn(
                'mt-[10px] inline-flex h-[30px] items-center gap-[7px] rounded-[9px] px-[14px] font-semibold text-[12px]',
                PRESSABLE
              )}
              style={{
                backgroundColor: V3_COLORS.amber,
                color: V3_COLORS.amberChipText,
              }}
            >
              <RotateCcw strokeWidth={2.4} className="size-[13px]" />
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  // SAND family (mic_wait / reconnecting) — reuses call-notice.tsx's
  // PATIENCE anatomy/tokens verbatim (see file header).
  const Icon = SAND_ICON[variant];
  return (
    <div
      role="status"
      className={cn(
        'relative overflow-hidden rounded-[14px] border-[1px] border-[#E8E1D4] bg-[#F5F1EA] px-[14px] pb-[13px] pt-[12px]',
        releasing
          ? 'motion-safe:animate-call-notice-release motion-reduce:animate-call-notice-release-reduced'
          : 'motion-safe:animate-patience-in motion-reduce:animate-patience-in-reduced',
        className
      )}
      style={{ boxShadow: '0 16px 34px -22px rgba(49,54,78,.4)' }}
    >
      <div className="flex items-start gap-[11px]">
        <span
          aria-hidden="true"
          className="mt-[1px] flex size-[30px] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: V3_COLORS.sandNoticeIconBg }}
        >
          <Icon
            strokeWidth={2}
            className="size-[15px]"
            style={{ color: V3_COLORS.muted }}
          />
        </span>
        <div className="min-w-0 flex-1 pb-[3px]">
          <div className="text-[13.5px] font-semibold leading-[1.4] text-[#31364E]">
            {copy.title}
          </div>
          <div className="mt-[2px] text-[12px] leading-[1.45] text-[#8a7c68]">
            {copy.subtitle}
          </div>
          {/* [Deviation from spec — see file header] `reconnecting` alone
              gets a subtle retry affordance; `mic_wait` never does (nothing
              to retry — the browser prompt is the only pending action). */}
          {variant === 'reconnecting' && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={cn(
                'mt-[8px] inline-flex items-center gap-[5px] text-[11.5px] font-semibold',
                PRESSABLE
              )}
              style={{ color: T13_STATUS_COLORS.connectingLabel }}
            >
              <RotateCcw strokeWidth={2.4} className="size-[11px]" />
              Try again
            </button>
          )}
        </div>
      </div>
      <PatienceSweepTrack heightPx={3} />
    </div>
  );
}
